'use client';

import { forwardRef } from 'react';
import { Chip as MantineChip } from '@mantine/core';
import type {
  ChipGroupProps as MantineChipGroupProps,
  ChipProps as MantineChipProps,
} from '@mantine/core';

import type { LockedProps } from '../types';

export type ChipProps = Omit<MantineChipProps, LockedProps>;

export type ChipGroupProps<Multiple extends boolean = false> = MantineChipGroupProps<Multiple>;

const ChipRoot = forwardRef<HTMLInputElement, ChipProps>((props, ref) => (
  <MantineChip ref={ref} {...props} />
));

ChipRoot.displayName = 'Chip';

const ChipGroup = <Multiple extends boolean = false>(props: ChipGroupProps<Multiple>) => (
  <MantineChip.Group {...props} />
);

ChipGroup.displayName = 'Chip.Group';

type ChipRootComponent = typeof ChipRoot;

interface ChipCompound extends ChipRootComponent {
  /**
   * Context wrapper managing the selected value(s) of its child chips. Set `multiple`
   * for array values.
   *
   * @example
   * <Chip.Group multiple value={selected} onChange={setSelected}>
   *   <Chip value="a">A</Chip>
   * </Chip.Group>
   */
  Group: typeof ChipGroup;
}

/**
 * ### Chip
 *
 * @description
 * Selectable pill chip with locked visual styling: constant size, no check icon — the
 * checked state only recolors the pill. Use `Chip.Group` for single or multi selection.
 *
 * @example
 * <Chip.Group multiple value={selected} onChange={setSelected}>
 *   <Chip value="a">Option A</Chip>
 * </Chip.Group>
 *
 * @param props - Locked chip props without direct styling escape hatches.
 * @returns Compound chip component (`Chip` and `Chip.Group`).
 */
export const Chip: ChipCompound = Object.assign(ChipRoot, {
  Group: ChipGroup,
});
